import React, { useState } from 'react';
import { ExternalLink, Maximize2, RefreshCw } from 'lucide-react';

const TabSystem = ({ 
  tabs = [], 
  defaultTab = null, 
  className = '',
  style = 'modern', // 'modern', 'classic', 'minimal'
  onTabChange = null,
  showIframeControls = true
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || (tabs.length > 0 ? tabs[0].id : null));
  const [iframeLoading, setIframeLoading] = useState({});
  const [iframeError, setIframeError] = useState({});

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleIframeLoad = (tabId) => {
    setIframeLoading(prev => ({ ...prev, [tabId]: false }));
    setIframeError(prev => ({ ...prev, [tabId]: false }));
  };

  const handleIframeError = (tabId) => {
    setIframeLoading(prev => ({ ...prev, [tabId]: false }));
    setIframeError(prev => ({ ...prev, [tabId]: true }));
  };

  const refreshIframe = (tabId) => {
    setIframeLoading(prev => ({ ...prev, [tabId]: true }));
    setIframeError(prev => ({ ...prev, [tabId]: false }));
    // Force iframe refresh by updating its key
    const iframe = document.querySelector(`#iframe-${tabId}`);
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  const openInNewWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getActiveTab = () => tabs.find(tab => tab.id === activeTab);
  const activeTabData = getActiveTab();

  if (!tabs || tabs.length === 0) {
    return (
      <div className="tab-system-empty">
        <p>No tabs configured</p>
      </div>
    );
  }

  return (
    <div className={`tab-system tab-system-${style} ${className}`}>
      {/* Tab Navigation */}
      <div className="tab-nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            title={tab.description || tab.name}
          >
            {tab.icon && <tab.icon size={16} />}
            <span className="tab-name">{tab.name}</span>
            {tab.badge && <span className="tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTabData && (
          <div className="tab-panel">
            {/* Tab Header */}
            {(activeTabData.description || showIframeControls) && (
              <div className="tab-panel-header">
                <div className="tab-info">
                  {activeTabData.description && (
                    <p className="tab-description">{activeTabData.description}</p>
                  )}
                </div>
                {showIframeControls && activeTabData.url && (
                  <div className="tab-controls">
                    <button
                      className="control-button"
                      onClick={() => refreshIframe(activeTabData.id)}
                      title="Actualizar"
                      disabled={iframeLoading[activeTabData.id]}
                    >
                      <RefreshCw 
                        size={14} 
                        className={iframeLoading[activeTabData.id] ? 'spinning' : ''} 
                      />
                    </button>
                    <button
                      className="control-button"
                      onClick={() => openInNewWindow(activeTabData.url)}
                      title="Abrir en nueva ventana"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button
                      className="control-button"
                      onClick={() => {
                        const iframe = document.querySelector(`#iframe-${activeTabData.id}`);
                        if (iframe) {
                          iframe.requestFullscreen?.();
                        }
                      }}
                      title="Pantalla completa"
                    >
                      <Maximize2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Tab Content Area */}
            <div className="tab-panel-content">
              {activeTabData.url ? (
                <div className="iframe-container">
                  {iframeLoading[activeTabData.id] && (
                    <div className="iframe-loading">
                      <div className="loading-spinner"></div>
                      <p>Cargando dashboard...</p>
                    </div>
                  )}
                  
                  {iframeError[activeTabData.id] ? (
                    <div className="iframe-error">
                      <div className="error-content">
                        <h4>Error al cargar el dashboard</h4>
                        <p>No se pudo cargar el dashboard desde: {activeTabData.url}</p>
                        <button 
                          className="retry-button"
                          onClick={() => refreshIframe(activeTabData.id)}
                        >
                          Reintentar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <iframe
                      id={`iframe-${activeTabData.id}`}
                      src={activeTabData.url}
                      title={activeTabData.name}
                      className="looker-iframe"
                      onLoad={() => handleIframeLoad(activeTabData.id)}
                      onError={() => handleIframeError(activeTabData.id)}
                      allow="fullscreen"
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    />
                  )}
                </div>
              ) : activeTabData.content ? (
                <div className="custom-content">
                  {typeof activeTabData.content === 'function' 
                    ? activeTabData.content() 
                    : activeTabData.content
                  }
                </div>
              ) : (
                <div className="placeholder-content">
                  <div className="placeholder-message">
                    <h4>Dashboard en desarrollo</h4>
                    <p>Esta sección estará disponible próximamente con análisis avanzados.</p>
                    <div className="placeholder-info">
                      <strong>ID del Dashboard:</strong> {activeTabData.id}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .tab-system {
          width: 100%;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        /* Modern Style */
        .tab-system-modern .tab-nav {
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 0;
          display: flex;
          overflow-x: auto;
        }

        .tab-system-modern .tab-button {
          padding: 16px 24px;
          border: none;
          background: none;
          color: #64748b;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          border-bottom: 2px solid transparent;
        }

        .tab-system-modern .tab-button:hover {
          color: #475569;
          background: #f1f5f9;
        }

        .tab-system-modern .tab-button.active {
          color: #00CED1;
          border-bottom-color: #00CED1;
          background: white;
        }

        /* Classic Style */
        .tab-system-classic .tab-nav {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 4px;
          display: flex;
          gap: 4px;
        }

        .tab-system-classic .tab-button {
          padding: 12px 20px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #64748b;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tab-system-classic .tab-button:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        .tab-system-classic .tab-button.active {
          background: #00CED1;
          color: white;
          border-color: #00CED1;
        }

        /* Minimal Style */
        .tab-system-minimal .tab-nav {
          background: none;
          border-bottom: 1px solid #e2e8f0;
          padding: 0;
          display: flex;
          gap: 32px;
          padding-left: 16px;
        }

        .tab-system-minimal .tab-button {
          padding: 16px 0;
          border: none;
          background: none;
          color: #64748b;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          border-bottom: 2px solid transparent;
        }

        .tab-system-minimal .tab-button:hover {
          color: #475569;
        }

        .tab-system-minimal .tab-button.active {
          color: #1e293b;
          border-bottom-color: #00CED1;
        }

        /* Common tab styles */
        .tab-badge {
          background: #ef4444;
          color: white;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 10px;
          font-weight: 600;
        }

        .tab-content {
          min-height: 1000px;
          height: 1000px;
        }

        .tab-panel {
          height: 100%;
        }

        .tab-panel-header {
          padding: 16px 20px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fafbfc;
        }

        .tab-description {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        .tab-controls {
          display: flex;
          gap: 8px;
        }

        .control-button {
          padding: 6px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          color: #64748b;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .control-button:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #475569;
        }

        .control-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .tab-panel-content {
          position: relative;
          height: calc(100% - 60px);
          min-height: 750px;
        }

        .iframe-container {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .looker-iframe {
          width: 100%;
          height: 100%;
          border: none;
          background: white;
        }

        .iframe-loading {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          z-index: 10;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #f1f5f9;
          border-top: 3px solid #7c3aed;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .iframe-error {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: #fef2f2;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .error-content {
          text-align: center;
          padding: 32px;
          max-width: 400px;
        }

        .error-content h4 {
          color: #dc2626;
          margin-bottom: 8px;
          font-size: 18px;
        }

        .error-content p {
          color: #7f1d1d;
          margin-bottom: 16px;
          font-size: 14px;
        }

        .retry-button {
          background: #dc2626;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s ease;
        }

        .retry-button:hover {
          background: #b91c1c;
        }

        .placeholder-content {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: #f8fafc;
        }

        .placeholder-message {
          text-align: center;
          padding: 48px 32px;
          max-width: 400px;
        }

        .placeholder-message h4 {
          color: #1e293b;
          margin-bottom: 12px;
          font-size: 20px;
          font-weight: 600;
        }

        .placeholder-message p {
          color: #64748b;
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .placeholder-info {
          background: white;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 12px;
          color: #475569;
        }

        .custom-content {
          padding: 20px;
          height: 100%;
        }

        .tab-system-empty {
          padding: 40px;
          text-align: center;
          color: #64748b;
          background: #f8fafc;
          border-radius: 8px;
        }

        @media (max-width: 768px) {
          .tab-nav {
            overflow-x: auto;
          }
          
          .tab-panel-header {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
          }
          
          .tab-controls {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default TabSystem;